import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { forkJoin, of } from 'rxjs';
import { Organisme } from 'src/app/data/intervenant/models/organisme.model';
import { OrganismeType } from 'src/app/data/intervenant/models/organisme-type.model';
import { Structure } from 'src/app/data/intervenant/models/structure.model';
import { Pays } from 'src/app/data/intervenant/models/pays.model';
import { OrganismesService } from 'src/app/data/intervenant/services/organismes/organismes.service';
import { ReferencesService } from 'src/app/data/intervenant/services/references/references.service';
import { AuthService } from 'src/app/core/services/auth/auth.service';
import { LoaderService } from '../../../../../../../core/services/loader/loader.service';

@Component({
  selector: 'app-edit-organisme',
  templateUrl: './edit-organisme.component.html',
  styleUrls: ['./edit-organisme.component.scss'],
  standalone: false
})
export class EditOrganismeComponent implements OnInit {
  creation = false;
  listePays: Pays[] = [];
  structures: Structure[] = [];
  typesOrganismes: OrganismeType[] = [];

  private organisme!: Organisme;
  formGroup!: UntypedFormGroup;

  get type() { return this.formGroup.get('type'); }
  get structure() { return this.formGroup.get('structure'); }
  get raisonSociale() { return this.formGroup.get('raisonSociale'); }
  get siret() { return this.formGroup.get('siret'); }
  get adresse() { return this.formGroup.get('adresse'); }
  get codePostal() { return this.formGroup.get('codePostal'); }
  get ville() { return this.formGroup.get('ville'); }
  get pays() { return this.formGroup.get('pays'); }
  get cedex() { return this.formGroup.get('cedex'); }
  get telephone() { return this.formGroup.get('telephone'); }
  get portable() { return this.formGroup.get('portable'); }
  get fax() { return this.formGroup.get('fax'); }
  get mail() { return this.formGroup.get('mail'); }
  get logo() { return this.formGroup.get('logo'); }

  constructor(
    private readonly route: ActivatedRoute,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly organismeService: OrganismesService,
    private readonly referencesService: ReferencesService,
    private readonly router: Router,
    private readonly auth: AuthService,
    private readonly loaderService: LoaderService
  ) {}

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    const idOrganisme = idParam ? Number(idParam) : 0;
    this.loadData(idOrganisme);
  }

  loadData(idOrganisme: number): void {
    this.creation = !idOrganisme;

    forkJoin([
      !this.creation
        ? this.organismeService.getOrganisme(idOrganisme)
        : of(new Organisme()),
      this.referencesService.getReferences()
    ])
      .pipe(this.loaderService.operator())
      .subscribe(([organisme, references]) => {
        this.organisme = organisme;
        this.typesOrganismes = references.typesOrganismes;
        this.structures = references.structures;
        this.listePays = references.pays;
        this.buildForm(organisme);
      });
  }

  buildForm(organisme: Organisme): void {
    this.formGroup = this.formBuilder.group({
      type: [{ value: organisme.type, disabled: !!organisme.id }, Validators.required],
      structure: [organisme.structure],
      raisonSociale: [organisme.raisonSociale, Validators.required],
      siret: [organisme.siret],
      adresse: [organisme.adresse],
      codePostal: [organisme.codePostal],
      ville: [organisme.ville],
      pays: [organisme.pays],
      cedex: [organisme.cedex],
      telephone: [organisme.telephone],
      portable: [organisme.portable],
      fax: [organisme.fax],
      mail: [organisme.mail],
      logo: [organisme.logo]
    });

    if (!this.auth.hasRole('INTERVENANT_ORGANISME_MODIFICATION')) {
      // this.formGroup.disable();
    }
  }

  submit(): void {

    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      return;
    }

    const raw = this.formGroup.getRawValue();
    const organisme = new Organisme();

    organisme.type = raw.type;
    organisme.structure = raw.structure;
    organisme.raisonSociale = raw.raisonSociale;
    organisme.siret = raw.siret;
    organisme.adresse = raw.adresse;
    organisme.codePostal = raw.codePostal;
    organisme.ville = raw.ville;
    organisme.pays = raw.pays;
    organisme.cedex = raw.cedex;
    organisme.telephone = raw.telephone;
    organisme.portable = raw.portable;
    organisme.fax = raw.fax;
    organisme.mail = raw.mail;
    organisme.logo = raw.logo;

  if (this.creation) {
    this.organismeService.createOrganisme(organisme).subscribe((idOrganisme) => {
        this.navigateToOrganismesList();
    });
  } else {
    organisme.id = this.organisme.id;
    this.organismeService.modifyOrganisme(organisme).subscribe(() => {
        this.navigateToOrganismesList();
    });
  }

    if(this.canEditAbonnements) {
            this.navigateToAbonements(organisme.id);
        }
  }

    get canModifyOrganisme(): boolean {
        return this.auth.hasRole('INTERVENANT_ORGANISME_MODIFICATION');
    }

    get canEditAbonnements(): boolean {
        return this.auth.hasRole('INTERVENANT_ORGANISME_ABONNEMENT_ECRITURE');
    }

  private navigateToAbonements(idOrganisme: number): void {
    const url = (this.creation ? '' : '../') + '../edit-abonnements';
    this.router.navigate([url, idOrganisme], { relativeTo: this.route });
  }

  private navigateToOrganismesList(): void {
    const url = this.creation ? '../' : '../../';
    this.router.navigate([url], { relativeTo: this.route });
  }

  public onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) {
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(file);

    reader.onload = () => {
      const result = reader.result as string;
      this.organisme.logo = result;
      this.logo?.setValue(result);
      this.logo?.markAsDirty();
      this.logo?.markAsTouched();
    };
  }
}